import React, { useContext, useEffect, useState } from "react";
import { useRouter } from "next/router";
import { groupBookingValidationPassenger } from "@/dataLayer/groupBooking";
import { saveBookingInRedux } from "@/services/manageBooking";
import { trackUserActivity } from "@/services/trackUserActivity";
import { Button } from "components/UI/Button";
import { IconV2 } from "components/V2/atoms/IconV2/IconV2";
import { ModalV2 } from "components/V2/atoms/ModalV2/ModalV2";
import { TextFieldV2 } from "components/V2/atoms/TextFieldV2/TextFieldV2";
import { actionType, eventName } from "constants/events/eventName";
import { NOTIFICATION_MESSAGE_TYPE, STATUS } from "constants/variables";
import { BookingDetailContext } from "context/BookingDetailsContext";
import { CommonDataContext } from "context/CommonDataContext";
import { screenContext } from "context/ScreenContext";
import { extractResource } from "helper/extractResource";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { displayToastNotification } from "@/utils/helperFunctions/displayToastNotification";
import { setPassengersKeys } from "@/store/slices/groupBooking";
interface ManageBookingPopUpGclassProps {
  modalTitle: string;
  open: boolean;
  onClose: () => void;
  showLastName?:  boolean;
  pageType?: string
  [key: string]: any;
}

type userName = {
  firstName: string;
  lastName: string;
};

function ManageBookingPopUpGclass(props: ManageBookingPopUpGclassProps) {
  const { modalTitle, open, onClose, showLastName = false, pageType, ...rest } = props;
  const { userInputs, groupBoardingPass, isPrimaryContact } = useSelector((state: any) => state.persistedReducers.groupBooking);
  const { currentDevice } = useContext(screenContext);
  const { resources } = useContext(CommonDataContext);
  const { setSelectedCurrency } = useContext(CommonDataContext);
  const { setCurrentBookingInStateData } = useContext(BookingDetailContext);

  const [userName, setUserName] = useState<userName>({
    firstName: "",
    lastName: ""
  });

  const [isLoader, setIsLoader] = useState<boolean>(false);
  const [errorTexts, setErrorTexts] = useState<string | boolean>(false);
  const [isShowLastName, setShowLastName] = useState<boolean>(showLastName);

  const dispatch = useDispatch();
  const router = useRouter();

  const handleClose = () =>{
    onClose();
    setErrorTexts(false);
    setUserName({ firstName: "", lastName: "" });
  }

  const handleChange = (event: { target: { name: string; value: string } }) => {
    const { name, value } = event.target;
    setUserName((preValues) => ({ ...preValues, [name]: value }));
  };

  const toastNotification = (toasterType: string) => {
    displayToastNotification(NOTIFICATION_MESSAGE_TYPE.ERROR, toasterType);
  };

  const handleSubmit = async () => {
    const { pnrDetails, lastName } = userInputs;
    setIsLoader(true);

    const param = {
      firstName: userName?.firstName,
      lastName: isShowLastName ? userName?.lastName : lastName,
      recordLocator: pnrDetails
    };

    try {
      const currentBookingState = await groupBookingValidationPassenger(param);

      // Check for validation failure
      if (currentBookingState?.status === false) {
        setErrorTexts(true);
        return;
      }

      setErrorTexts(false);

      // Check if booking is cancelled
      const isCancelled = currentBookingState?.info?.status === STATUS.CLOSED && currentBookingState?.journeys?.length === 0;
      if (isCancelled && !currentBookingState?.allowManageBooking) {
        toastNotification(extractResource(resources, "bookingCancelled"));
        return;
      }

      // Check for check-in restrictions
      if (currentBookingState?.restriction?.allowCheckIn === false) {
        toastNotification(extractResource(resources, "noBookingFound"));
        return;
      }

      // Handle confirmed bookings
      if (currentBookingState?.info?.status === STATUS.CONFIRMED) {
        trackUserActivity({
          actionType: actionType.CLICK,
          eventName: eventName.RETRIVE_BOOKING_WITH_PNR,
          details: { userInput: param, pageType, currentBookingInState: currentBookingState, selectedCurrency: currentBookingState?.currencyCode} //pageType needs to be add
        });
        setSelectedCurrency(currentBookingState?.currencyCode);
        saveBookingInRedux({ currentBooking: currentBookingState, refreshBookingData: true, dispatch }); //setting in a Redux 
        setCurrentBookingInStateData({data: {data: currentBookingState } }); //setting in Booking Detail Context 

        const passengerKeys = currentBookingState?.passengers?.map((passenger: any) => passenger.passengerKey );

        sessionStorage.setItem("passnegerKeys", JSON.stringify(passengerKeys));
        dispatch(setPassengersKeys(passengerKeys));
      }

      // Navigate to manage booking if journeys exist
      if (currentBookingState.journeys.length > 0) {
        if(groupBoardingPass){
          router.push("/boarding-pass");
        }else{
          router.push("/manage-booking/retrieve-booking");
        }
      } else if (isCancelled) {
        toastNotification(extractResource(resources, "bookingCancelled"));
      } else {
        toastNotification(extractResource(resources, "somethingWentWrong"));
      }

    } catch (error) {
      console.error("Error during booking validation:", error);
      toastNotification(extractResource(resources, "noBookingFound")); 
    } finally {
      setIsLoader(false); // Ensure loader is turned off in all cases
    }
  };

  const modalHeaderMweb = () => {
    return (
      <div id="modalHeader" className="flex gap-3 py-1.5">
        {currentDevice === "mobile" && (
          <button onClick={handleClose} type="button">
            <IconV2
              blok={{
                component: "icon",
                icon: "chevron-left",
                height: 20,
                width: 20,
                alt: "clear Icon"
              }}
            />
          </button>
        )}
        {extractResource(resources, modalTitle)}
      </div>
    );
  };

  useEffect(() => {
    if((userInputs?.email && userInputs?.email.includes("@") || showLastName || isPrimaryContact)){
      setShowLastName(true);
    } else {
      setShowLastName(false);
    }
}, [userInputs, userInputs?.email, showLastName, isPrimaryContact])

  return (
    <ModalV2
      isOpen={open}
      onClose={handleClose}
      modalTitle={modalHeaderMweb()}
      size="sm"
      modalWidth={`${currentDevice === "mobile" ? "auto" :  `${isShowLastName ? "620px" : "466px" }`}`}
      classNamechildren="flex flex-col justify-between pb-2 px-5"
      setModalBottom={true}
      showBorderRadius={currentDevice === "mobile" ? false : true}
      modalHeight={currentDevice === "mobile" ? "100vh" : ""}
      maxHeight={currentDevice === "mobile" ? "100vh" : ""}
      hideCloseIcon={currentDevice === "mobile" ? true : false}
      additionalClassHeader={currentDevice === "mobile" ? "shadow-2xl shadow-[#0000001A] drop-shadow-xl" : ""}
      {...rest}
    >
      <div>
        <div className="mt-4 sm:mt-0 text-sm">{extractResource(resources, "gClassBookingMsg")}</div>
        <div className="flex flex-col sm:flex-row gap-3 mt sm:mt-4">
          <TextFieldV2
            label="First Name"
            name="firstName"
            value={userName.firstName}
            onChange={handleChange}
            autoComplete="off"
            additionalWrapperClassNames="w-full"
            additionalInputClassNames={`capitalize ${errorTexts ? '!border-[#D31313]' : ''}`}
            viewHeight="h-14 pt-1"
            additionalLabelClassNames="pt-4"
            variant="underline"
          />
          {isShowLastName && <TextFieldV2
            label="Last Name"
            name="lastName"
            value={userName.lastName}
            onChange={handleChange}
            autoComplete="off"
            additionalWrapperClassNames="w-full"
            additionalInputClassNames={`capitalize ${errorTexts ? '!border-[#D31313]' : ''}`}
            viewHeight="h-14 pt-1"
            additionalLabelClassNames="pt-4"
            variant="underline"
          />}
        </div>
        {errorTexts && (
          <div className="flex text-typography-danger break-normal hyphens-manual text-[12px] mt-3 w-full">
            <IconV2
              blok={{
                component: "icon",
                icon: "red-alert-info",
                height: 12,
                width: 12,
                alt: "Red alert info"
              }}
            />
            <span className="ml-1">
              {extractResource(resources, isShowLastName ? "enterFirstAndLastNameMsg" : "enterFirstNameMsg")}
            </span>
          </div>
        )}
      </div>
      <div className="sm:px-6 py-2 sm:pb-4 sm:pt-6 flex justify-center">
        <Button
          className="text-sm md:text-base w-full sm:w-auto"
          onClick={handleSubmit}
          disabled={isShowLastName ? userName.firstName.trim() === "" || userName.lastName.trim() === "" : userName.firstName.trim() === ""}
          variant="contained"
          color="primary"
          isLoading={isLoader}
          aria-label={extractResource(resources, "submit")}
        >
          {extractResource(resources, "submit")}
        </Button>
      </div>
    </ModalV2>
  );
}

export default ManageBookingPopUpGclass;